import { OverlayConfig, Overlay, OverlayModule as OverlayModule$1 } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { InjectionToken, Injectable, Injector, Directive, Optional, ElementRef, HostBinding, Input, HostListener, NgModule } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { PortalInjector, ComponentPortal } from '@angular/cdk/portal';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@angular/cdk/overlay';
let dialogElementUid = 0;
class ImpactOverlayRef {
    /**
     * @param {?} overlayRef
     */
    constructor(overlayRef) {
        this.overlayRef = overlayRef;
        this.id = `impact-dialog-${dialogElementUid++}`;
        this._beforeClose = new Subject();
        this._afterClose = new Subject();
        this.cdkRef = this.overlayRef;
    }
    /**
     * @param {?=} dialogResult
     * @return {?}
     */
    close(dialogResult) {
        if (this.componentInstance.animationStateChanged) {
            /**
             * Detaching the overlay
             */
            this.componentInstance.animationStateChanged
                .pipe(filter((/**
             * @param {?} event
             * @return {?}
             */
            (event) => event.phaseName === 'start')), first())
                .subscribe((/**
             * @return {?}
             */
            () => {
                this._beforeClose.next(dialogResult);
                this._beforeClose.complete();
                this.overlayRef.detachBackdrop();
            }));
            /**
             * Disposing the overlay and cleaning up
             */
            this.componentInstance.animationStateChanged
                .pipe(filter((/**
             * @param {?} event
             * @return {?}
             */
            (event) => {
                return event.phaseName === 'done' && event.toState === 'leave';
            })), first())
                .subscribe((/**
             * @return {?}
             */
            () => {
                this.overlayRef.dispose();
                this._afterClose.next(dialogResult);
                this._afterClose.complete();
                // Make sure to also clear the reference to the
                // component instance to avoid memory leaks
                this.componentInstance = null;
            }));
            this.componentInstance.startExitAnimation();
        }
        else {
            this._beforeClose.next(dialogResult);
            this._beforeClose.complete();
            this.overlayRef.dispose();
            this._afterClose.next(dialogResult);
            this._afterClose.complete();
        }
    }
    /**
     * @return {?}
     */
    beforeClose() {
        return this._beforeClose.asObservable();
    }
    /**
     * @return {?}
     */
    afterClose() {
        return this._afterClose.asObservable();
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
const IMPACT_OVERLAY_DATA = new InjectionToken('IMPACT_OVERLAY_DATA');

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
const ɵ0 = {};
/** @type {?} */
const DEFAULT_CONFIG = {
    hasBackdrop: true,
    blockScroll: true,
    backdropClass: 'cdk-overlay-dark-backdrop',
    panelClass: '',
    positionVertical: {
        placement: 'center',
    },
    positionHorizontal: {
        placement: 'center',
    },
    data: ɵ0,
};
class OverlayService {
    /**
     * @param {?} overlay
     * @param {?} injector
     */
    constructor(overlay, injector) {
        this.overlay = overlay;
        this.injector = injector;
        /**
         * Internal holder of {ImpactOverlayRef}
         */
        this.refs = [];
        this.positionStrategies = this.overlay.position();
    }
    /**
     * @param {?} component
     * @param {?=} config
     * @return {?}
     */
    open(component, config = {}) {
        /** @type {?} */
        const dialogConfig = Object.assign({}, DEFAULT_CONFIG, config);
        /** @type {?} */
        const overlayRef = this.createOverlay(dialogConfig);
        /** @type {?} */
        const dialogRef = new ImpactOverlayRef(overlayRef);
        /** @type {?} */
        const overlayComponent = this.attachDialogContainer(overlayRef, dialogConfig, dialogRef, component);
        dialogRef.componentInstance = overlayComponent;
        this.refs.push(dialogRef);
        dialogRef
            .beforeClose()
            .pipe(first())
            .subscribe((/**
         * @return {?}
         */
        () => this.refs.splice(this.refs.indexOf(dialogRef), 1)));
        overlayRef.backdropClick().subscribe((/**
         * @return {?}
         */
        () => dialogRef.close()));
        return dialogRef;
    }
    /**
     * Close all panes what so ever
     * @return {?}
     */
    closeAll() {
        if (this.refs.length > 0) {
            this.refs.forEach((/**
             * @param {?} child
             * @return {?}
             */
            child => child.close()));
            this.refs.splice(0);
        }
    }
    /**
     * @param {?} config
     * @return {?}
     */
    getOverlayConfig(config) {
        /** @type {?} */
        let positionStrategy = config.positionStrategy;
        if (!positionStrategy) {
            /** @type {?} */
            const globalPositionStrategy = this.overlay.position().global();
            if (config.positionHorizontal) {
                switch (config.positionHorizontal.placement) {
                    case 'left':
                        config.positionHorizontal.offset
                            ? globalPositionStrategy.left(config.positionHorizontal.offset)
                            : globalPositionStrategy.left();
                        break;
                    case 'right':
                        config.positionHorizontal.offset
                            ? globalPositionStrategy.right(config.positionHorizontal.offset)
                            : globalPositionStrategy.right();
                        break;
                    case 'center':
                        config.positionHorizontal.offset
                            ? globalPositionStrategy.centerHorizontally(config.positionHorizontal.offset)
                            : globalPositionStrategy.centerHorizontally();
                        break;
                }
            }
            if (config.positionVertical) {
                switch (config.positionVertical.placement) {
                    case 'top':
                        config.positionVertical.offset
                            ? globalPositionStrategy.top(config.positionVertical.offset)
                            : globalPositionStrategy.top();
                        break;
                    case 'bottom':
                        config.positionVertical.offset
                            ? globalPositionStrategy.bottom(config.positionVertical.offset)
                            : globalPositionStrategy.bottom();
                        break;
                    case 'center':
                        config.positionVertical.offset
                            ? globalPositionStrategy.centerVertically(config.positionVertical.offset)
                            : globalPositionStrategy.centerVertically();
                        break;
                }
            }
            positionStrategy = globalPositionStrategy;
        }
        /** @type {?} */
        let scrollStrategy = config.scrollStrategy;
        if (!scrollStrategy) {
            scrollStrategy = config.blockScroll
                ? this.overlay.scrollStrategies.block()
                : this.overlay.scrollStrategies.noop();
        }
        /** @type {?} */
        const panelClass = Array.isArray(config.panelClass)
            ? [...config.panelClass]
            : config.panelClass ? [config.panelClass] : [];
        panelClass.unshift('impact-dialog-container');
        panelClass.push('impact-panel');
        if (config.fullHeight) {
            panelClass.push('impact-panel--full-height');
        }
        return new OverlayConfig({
            hasBackdrop: config.hasBackdrop,
            backdropClass: config.backdropClass,
            panelClass,
            scrollStrategy,
            positionStrategy,
        });
    }
    /**
     * @param {?} config
     * @return {?}
     */
    createOverlay(config) {
        /** @type {?} */
        const overlayConfig = this.getOverlayConfig(config);
        return this.overlay.create(overlayConfig);
    }
    /**
     * @param {?} config
     * @param {?} dialogRef
     * @return {?}
     */
    createInjector(config, dialogRef) {
        /** @type {?} */
        const injectionTokens = new WeakMap();
        injectionTokens.set(ImpactOverlayRef, dialogRef);
        injectionTokens.set(IMPACT_OVERLAY_DATA, config.data);
        return new PortalInjector(this.injector, injectionTokens);
    }
    /**
     * @param {?} overlayRef
     * @param {?} config
     * @param {?} dialogRef
     * @param {?} component
     * @return {?}
     */
    attachDialogContainer(overlayRef, config, dialogRef, component) {
        /** @type {?} */
        const injector = this.createInjector(config, dialogRef);
        /** @type {?} */
        const containerPortal = new ComponentPortal(component, null, injector);
        /** @type {?} */
        const containerRef = overlayRef.attach(containerPortal);
        return containerRef.instance;
    }
}
/** @nocollapse */
OverlayService.ɵfac = function OverlayService_Factory(t) { return new (t || OverlayService)(ɵngcc0.ɵɵinject(ɵngcc1.Overlay), ɵngcc0.ɵɵinject(ɵngcc0.Injector)); };
/** @nocollapse */
OverlayService.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: OverlayService, factory: OverlayService.ɵfac });
/** @nocollapse */
OverlayService.ctorParameters = () => [
    { type: Overlay },
    { type: Injector }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(OverlayService, [{
        type: Injectable
    }], function () { return [{ type: ɵngcc1.Overlay }, { type: ɵngcc0.Injector }]; }, null); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class ImpDialogCloseDirective {
    /**
     * @param {?} dialogRef
     * @param {?} elementRef
     * @param {?} overlay
     */
    constructor(dialogRef, elementRef, overlay) {
        this.dialogRef = dialogRef;
        this.elementRef = elementRef;
        this.overlay = overlay;
        this.ariaLabel = 'Close dialog';
        this.buttonType = 'button';
    }
    /**
     * @return {?}
     */
    ngOnInit() {
        if (!this.dialogRef) {
            this.dialogRef = getClosestDialog(this.elementRef, this.overlay.refs);
        }
    }
    /**
     * @param {?} changes
     * @return {?}
     */
    ngOnChanges(changes) {
        if (changes._impDialogClose) {
            this.dialogResult = changes._impDialogClose.currentValue;
        }
    }
    /**
     * @return {?}
     */
    onClick() {
        this.dialogRef.close(this.dialogResult);
    }
}
/** @nocollapse */
ImpDialogCloseDirective.ɵfac = function ImpDialogCloseDirective_Factory(t) { return new (t || ImpDialogCloseDirective)(ɵngcc0.ɵɵdirectiveInject(ImpactOverlayRef, 8), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ElementRef), ɵngcc0.ɵɵdirectiveInject(OverlayService)); };
/** @nocollapse */
ImpDialogCloseDirective.ɵdir = /*@__PURE__*/ ɵngcc0.ɵɵdefineDirective({ type: ImpDialogCloseDirective, selectors: [["button", "impDialogClose", ""], ["button", "imp-dialog-close", ""]], hostVars: 2, hostBindings: function ImpDialogCloseDirective_HostBindings(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵlistener("click", function ImpDialogCloseDirective_click_HostBindingHandler() { return ctx.onClick(); });
    } if (rf & 2) {
        ɵngcc0.ɵɵhostProperty("type", ctx.buttonType);
        ɵngcc0.ɵɵattribute("aria-label", ctx.ariaLabel);
    } }, inputs: { ariaLabel: ["aria-label", "ariaLabel"], dialogResult: ["imp-dialog-close", "dialogResult"], _impDialogClose: ["impDialogClose", "_impDialogClose"] }, exportAs: ["impDialogClose"], features: [ɵngcc0.ɵɵNgOnChangesFeature] });
/** @nocollapse */
ImpDialogCloseDirective.ctorParameters = () => [
    { type: ImpactOverlayRef, decorators: [{ type: Optional }] },
    { type: ElementRef },
    { type: OverlayService }
];
ImpDialogCloseDirective.propDecorators = {
    ariaLabel: [{ type: HostBinding, args: ['attr.aria-label',] }, { type: Input, args: ['aria-label',] }],
    buttonType: [{ type: HostBinding, args: ['type',] }],
    dialogResult: [{ type: Input, args: ['imp-dialog-close',] }],
    _impDialogClose: [{ type: Input, args: ['impDialogClose',] }],
    onClick: [{ type: HostListener, args: ['click',] }]
};
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(ImpDialogCloseDirective, [{
        type: Directive,
        args: [{
                selector: 'button[impDialogClose], button[imp-dialog-close]',
                exportAs: 'impDialogClose'
            }]
    }], function () { return [{ type: ImpactOverlayRef, decorators: [{
                type: Optional
            }] }, { type: ɵngcc0.ElementRef }, { type: OverlayService }]; }, { ariaLabel: [{
            type: HostBinding,
            args: ['attr.aria-label']
        }, {
            type: Input,
            args: ['aria-label']
        }], buttonType: [{
            type: HostBinding,
            args: ['type']
        }], dialogResult: [{
            type: Input,
            args: ['imp-dialog-close']
        }], 
    /**
     * @return {?}
     */
    onClick: [{
            type: HostListener,
            args: ['click']
        }], _impDialogClose: [{
            type: Input,
            args: ['impDialogClose']
        }] }); })();
/**
 * Finds the closest ImpactOverlayRef to an element by looking at the DOM.
 * @param {?} element Element relative to which to look for a dialog.
 * @param {?} openDialogs References to the currently-open dialogs.
 * @return {?}
 */
function getClosestDialog(element, openDialogs) {
    /** @type {?} */
    let parent = element.nativeElement.parentElement;
    while (parent && !parent.classList.contains('impact-dialog-container')) {
        parent = parent.parentElement;
    }
    return parent ? openDialogs.find((/**
     * @param {?} dialog
     * @return {?}
     */
    dialog => dialog.id === (/** @type {?} */ (parent)).id)) : null;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class OverlayModule {
}
/** @nocollapse */
OverlayModule.ɵfac = function OverlayModule_Factory(t) { return new (t || OverlayModule)(); };
/** @nocollapse */
OverlayModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: OverlayModule });
/** @nocollapse */
OverlayModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({ providers: [OverlayService], imports: [CommonModule, OverlayModule$1] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(OverlayModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, OverlayModule$1],
                providers: [OverlayService],
                declarations: [ImpDialogCloseDirective],
                exports: [ImpDialogCloseDirective]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(OverlayModule, { declarations: function () { return [ImpDialogCloseDirective]; }, imports: function () { return [CommonModule, OverlayModule$1]; }, exports: function () { return [ImpDialogCloseDirective]; } }); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/**
 * @abstract
 */
class ImpactOverlayLeave {
}

export { IMPACT_OVERLAY_DATA, ImpDialogCloseDirective, ImpactOverlayLeave, ImpactOverlayRef, OverlayModule, OverlayService };

